import { Document, Page, View } from "@react-pdf/renderer";
import SFRHeader from "./SoilAndFertilizerReportPDFComponents/SFReader";
import SFRMetadata from "./SoilAndFertilizerReportPDFComponents/SFRMetadata";
import SFRSoilHealthStatus from "./SoilAndFertilizerReportPDFComponents/SFRSoilHealthStatus";
import SFRGeneralRecommendation from "./SoilAndFertilizerReportPDFComponents/SFRGeneralRecommendation";
import SFRCropFertilizerRecommendation from "./SoilAndFertilizerReportPDFComponents/SFRCropFertilizerRecommendation";
import SFROfraCalculatedValue from "./SoilAndFertilizerReportPDFComponents/SFROfraCalculatedValue";
import SFRFooter from "./SoilAndFertilizerReportPDFComponents/SFRFooter";
import { useTranslation } from "react-i18next";
import { calculateOfraFertilizerRate, formatOfraNumber } from "../../utils/ofraFertilizerRateCalculation";
import {
  getContainerLabel,
  getFertilizerTypeLabel,
  getApplicationMethodLabel,
} from "../../utils/ofraDisplayFormatter";

// ──────────────────────────────────────────────────────────────────────────
// SoilAndFertilizerReportPDF
//
// This component builds the PDF report that gets downloaded.
// It includes sections for:
// - Soil health status
// - General recommendations
// - Crop-based fertilizer recommendation (if the farmer selected automatic mode)
// - OFRA calculated values (if the farmer used the manual OFRA calculator)
// ──────────────────────────────────────────────────────────────────────────
function SoilAndFertilizerReportPDF({
  placeName,
  coordinates,
  soilData,
  recommendation,
  selectedCrop,
  selectedBlend,
  cropRecommendation,
  // OFRA values from the manual calculator
  ofraContainerValue,
  ofraFertilizerTypeValue,
  ofraFertilizerRateValue,
  ofraApplicationMethodValue,
}) {
  const { t } = useTranslation();

  let cropBasedRecommendation;

  switch (cropRecommendation) {
    case "Unable to calculate recommendation for the selected crop and blend.":
      cropBasedRecommendation = t("cropFertilizerRecommendation.unableToCalculate");
      break;

    case "No fertilizer application is required.":
      cropBasedRecommendation = t("cropFertilizerRecommendation.noApplicationRequired");
      break;

    default:
      cropBasedRecommendation = `${t("common.apply")} ${cropRecommendation} ${t("common.perHectare")}`;
      break;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // OFRA Calculation
  // If the farmer filled in the OFRA calculator in manual mode, calculate and prepare the values
  // ──────────────────────────────────────────────────────────────────────────
  const ofraCalculation = calculateOfraFertilizerRate({
    containerValue: ofraContainerValue,
    fertilizerTypeValue: ofraFertilizerTypeValue,
    fertilizerRateValue: ofraFertilizerRateValue,
    applicationMethodValue: ofraApplicationMethodValue,
  });

  // Check if all OFRA fields have been filled in and the calculation is valid
  const hasCompleteOfraInput =
    ofraContainerValue &&
    ofraFertilizerTypeValue &&
    ofraFertilizerRateValue &&
    ofraApplicationMethodValue &&
    ofraCalculation.status === "valid";

  // Prepare OFRA values for display in the PDF (only if calculation is valid)
  const resultTranslationKey =
    ofraApplicationMethodValue === "point"
      ? "ofra.pointsPerContainer"
      : "ofra.metersPerContainer";

  // Prepare the display data for the OFRA section in the PDF
  const ofraDisplayData = hasCompleteOfraInput
    ? {
        containerLabel: getContainerLabel(ofraContainerValue),
        fertilizerLabel: getFertilizerTypeLabel(ofraFertilizerTypeValue),
        fertilizerRate: `${ofraFertilizerRateValue} kg/ha`,
        applicationMethod: getApplicationMethodLabel(ofraApplicationMethodValue),
        resultLabel: t(resultTranslationKey),
        result: formatOfraNumber(ofraCalculation.calculatedValue),
      }
    : null;

  return (
    <Document>
      <Page
        size="A4"
        style={{
          padding: 32,
          fontFamily: "Helvetica",
          fontSize: 11,
          color: "#1f2937",
        }}
      >
        {/* Header */}
        <SFRHeader />

        {/* Metadata */}
        <SFRMetadata placeName={placeName} coordinates={coordinates} />

        {/* Divider */}
        <View
          style={{
            borderBottom: "1px solid #e2e8f0",
            marginVertical: 12,
          }}
        />

        {/* Soil Health Status */}
        <SFRSoilHealthStatus soilData={soilData} recommendation={recommendation} />

        {/* Divider */}
        <View
          style={{
            borderBottom: "1px solid #e2e8f0",
            marginVertical: 12,
          }}
        />

        {/* General Recommendations */}
        <SFRGeneralRecommendation recommendation={recommendation} />

        {/* Crop Based Fertilizer Recommendation - ONLY if all props are truthy */}
        {selectedCrop && selectedBlend && cropRecommendation && (
          <>
            {/* Divider */}
            <View
              style={{
                borderBottom: "1px solid #e2e8f0",
                marginVertical: 12,
              }}
            />

            {/* Crop Based Fertilizer Recommendation */}
            <SFRCropFertilizerRecommendation
              selectedCrop={selectedCrop}
              selectedBlend={selectedBlend}
              cropRecommendation={cropBasedRecommendation}
            />
          </>
        )}

        {/* Ofra Calculated Value — ONLY if the farmer filled in all OFRA fields */}
        {ofraDisplayData && (
          <>
            {/* Divider */}
            <View
              style={{
                borderBottom: "1px solid #e2e8f0",
                marginVertical: 12,
              }}
            />

            {/* OFRA Results Section */}
            <SFROfraCalculatedValue
              volumeContainer={ofraDisplayData.containerLabel}
              fertilizerType={ofraDisplayData.fertilizerLabel}
              fertilizerRate={ofraDisplayData.fertilizerRate}
              applicationMethod={ofraDisplayData.applicationMethod}
              resultLabel={ofraDisplayData.resultLabel}
              result={ofraDisplayData.result}
            />
          </>
        )}

        {/* Footer */}
        <SFRFooter />
      </Page>
    </Document>
  );
}

export default SoilAndFertilizerReportPDF;